import React from 'react'

import { BaseText } from '@/components/base'
import { useTheme } from '@/hooks/useTheme'
import { useWindowDimensions } from '@/hooks/useWindowDimensions'
import { responsiveValueAdapter } from '@/utils'
import { HomeSlidersEnumID } from '@/constants/homeSliders'
import { AppIcons } from '@/components/AppIcons'

interface SliderItemProps {
  id: HomeSlidersEnumID
  title: string
  description?: string
  titleFontSize?: number
  descriptionFontSize?: number
  backgroundImage: string
  internalImage: string
  descriptionIconName?: string
  maxContentWidth?: number
  onPress: () => void
}

export const SliderItem: React.FC<SliderItemProps> = ({
  id,
  title,
  titleFontSize = 20,
  description,
  descriptionFontSize = 12,
  backgroundImage,
  internalImage,
  descriptionIconName,
  maxContentWidth = 160,
  onPress,
}) => {
  const theme = useTheme()
  const { width: screenWidth, height: screenHeight } = useWindowDimensions()

  // Card dimensions like in original (115px height) - now responsive to screen changes
  const cardHeight =
    responsiveValueAdapter(115, true, screenWidth, screenHeight) || 115
  const cardWidth = screenWidth * 0.931 // 96% of screen width

  // Internal image styles based on slider type (from RN styles)
  const getInternalImageStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      position: 'absolute',
      width: '100%',
    }

    switch (id) {
      case HomeSlidersEnumID.addPhoneNumber:
        return {
          ...baseStyles,
          transform: 'rotate(31.943deg)',

          top: `${-(responsiveValueAdapter(13, false, screenWidth, screenHeight) ?? 13)}px`,
          left: `${responsiveValueAdapter(221, false, screenWidth, screenHeight) ?? 221}px`,
          height: `${responsiveValueAdapter(168, true, screenWidth, screenHeight) ?? 168}px`,
          width: 'auto',
          aspectRatio: '112.01/168.01',
        }
      case HomeSlidersEnumID.verifyEmail:
        return {
          ...baseStyles,
          right: `${-(responsiveValueAdapter(100, false, screenWidth, screenHeight) ?? 100)}px`,
          bottom: `${-(responsiveValueAdapter(85, true, screenWidth, screenHeight) ?? 85)}px`,
          height: `${responsiveValueAdapter(240, true, screenWidth, screenHeight) ?? 240}px`,
        }
      case HomeSlidersEnumID.verifyID:
        return {
          ...baseStyles,
          right: `${-(responsiveValueAdapter(60, false, screenWidth, screenHeight) ?? 60)}px`,
          height: `${responsiveValueAdapter(120, true, screenWidth, screenHeight) ?? 120}px`,
        }
      case HomeSlidersEnumID.inviteFriend:
        return {
          display: 'none',
        }
      case HomeSlidersEnumID.cashback:
        return {
          ...baseStyles,
          right: `-${responsiveValueAdapter(38, false, screenWidth, screenHeight) ?? 38}px`,
          bottom: `${-(responsiveValueAdapter(27, true, screenWidth, screenHeight) ?? 27)}px`,
          height: `${responsiveValueAdapter(157.5, true, screenWidth, screenHeight) ?? 157.5}px`,
          width: 'auto',
        }
      default:
        return baseStyles
    }
  }

  const getTitleStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {}

    switch (id) {
      case HomeSlidersEnumID.cashback:
        return {
          ...baseStyles,
          fontSize:
            responsiveValueAdapter(18, false, screenWidth, screenHeight) || 18,
        }
      default:
        return baseStyles
    }
  }

  const getContentStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {}

    switch (id) {
      case HomeSlidersEnumID.cashback:
        return {
          ...baseStyles,
          maxWidth: `${responsiveValueAdapter(289, false, screenWidth, screenHeight) || 289}px`,
          gap: `${responsiveValueAdapter(4, true, screenWidth, screenHeight) || 4}px`,
        }
      default:
        return baseStyles
    }
  }

  const getDescriptionStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {}

    switch (id) {
      case HomeSlidersEnumID.cashback:
        return {
          ...baseStyles,
          fontSize: `${responsiveValueAdapter(16, false, screenWidth, screenHeight) || 16}px`,
          paddingTop: `${responsiveValueAdapter(6, true, screenWidth, screenHeight) || 6}px`,
          paddingBottom: `${responsiveValueAdapter(6, true, screenWidth, screenHeight) || 6}px`,
          paddingLeft: `${responsiveValueAdapter(12, false, screenWidth, screenHeight) || 12}px`,
          paddingRight: `${responsiveValueAdapter(12, false, screenWidth, screenHeight) || 12}px`,
          fontWeight: 'medium',
          lineHeight: `${responsiveValueAdapter(22, true, screenWidth, screenHeight) || 22}px`,
          backgroundColor: theme.colors.background.semiTransparent,
          borderRadius: `${20}px`,
          maxWidth: `${responsiveValueAdapter(130, false, screenWidth, screenHeight) || 130}px`,
          textAlign: 'center',
          whiteSpace: 'nowrap',
        }
      default:
        return baseStyles
    }
  }

  return (
    <div
      style={{
        height: `${cardHeight}px`,
        width: `${cardWidth}px`,
        minWidth: `${cardWidth}px`, // Prevent shrinking
        borderRadius: '32px',
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer',
        paddingTop: `${responsiveValueAdapter(19, true, screenWidth, screenHeight) || 19}px`,
        paddingLeft: `${responsiveValueAdapter(16, false, screenWidth, screenHeight) || 16}px`,
        paddingRight: `${responsiveValueAdapter(16, false, screenWidth, screenHeight) || 16}px`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        scrollSnapAlign: 'start',
        backgroundColor: theme.colors.background.cardItemRoot,
      }}
      onClick={onPress}
    >
      {/* Background Image */}
      <img
        src={backgroundImage}
        alt=""
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: `${cardWidth}px`, // Use card width instead of 100%
          height: `${cardHeight}px`,
          objectFit: 'cover',
          borderRadius: '32px',
        }}
      />

      {/* Internal Image (phone, email, etc.) */}
      <img
        src={internalImage}
        alt=""
        style={{
          ...getInternalImageStyles(),
          objectFit: 'contain', // Prevent stretching
        }}
      />

      {/* Content */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          maxWidth: `${responsiveValueAdapter(maxContentWidth, false, screenWidth, screenHeight) || maxContentWidth}px`,
          display: 'flex',
          flexDirection: 'column',
          gap: `${responsiveValueAdapter(12, true, screenWidth, screenHeight) || 12}px`,
          width: '100%',
          ...getContentStyles(),
        }}
      >
        {/* Title */}
        <BaseText
          fontSize={
            responsiveValueAdapter(
              titleFontSize,
              false,
              screenWidth,
              screenHeight,
            ) || titleFontSize
          }
          fontFamily="semibold"
          color="label.default"
          style={{
            letterSpacing: '0.257px',
            lineHeight: '1.2',
            wordWrap: 'break-word',
            overflow: 'hidden',
            ...getTitleStyles(),
          }}
        >
          {title}
        </BaseText>

        {/* Description with optional icon */}
        {description && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
            }}
          >
            {descriptionIconName && (
              <AppIcons name={descriptionIconName} size={18} color="#000" />
            )}

            <BaseText
              fontSize={
                responsiveValueAdapter(
                  descriptionFontSize,
                  false,
                  screenWidth,
                  screenHeight,
                ) || descriptionFontSize
              }
              fontFamily="regular"
              color="label.default"
              style={{
                lineHeight: '1.2',
                wordWrap: 'break-word',
                overflow: 'hidden',
                flex: 1,
                ...getDescriptionStyles(),
              }}
            >
              {description}
            </BaseText>
          </div>
        )}
      </div>
    </div>
  )
}
